import { Component, ChangeDetectionStrategy, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { PublicSaleLinkDTO, SaleLinkService } from '../../../services/sale-link.service';

@Component({
  selector: 'app-public-sale-link-receipt',
  templateUrl: './public-sale-link-receipt.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule]
})
export class PublicSaleLinkReceiptComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private saleLinkService = inject(SaleLinkService);

  detail = signal<PublicSaleLinkDTO | null>(null);
  loading = signal(true);
  error = signal(false);
  paying = signal(false);

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    const hash = this.route.snapshot.queryParamMap.get('hash');
    if (!id || Number.isNaN(id) || !hash) {
      this.loading.set(false);
      this.error.set(true);
      return;
    }
    this.saleLinkService.getPublic(id, hash).subscribe({
      next: (response) => {
        if (response.body?.type === 'SUBSCRIPTION') {
          void this.router.navigate(['/public/sale-links', id, 'subscription'], {
            queryParams: { hash },
            replaceUrl: true
          });
          return;
        }
        this.detail.set(response.body);
        this.loading.set(false);
        if (!response.body) {
          this.error.set(true);
        }
      },
      error: () => {
        this.loading.set(false);
        this.error.set(true);
      }
    });
  }

  hasNotes(notes: string | undefined): boolean {
    if (!notes) {
      return false;
    }
    const plain = notes.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
    return plain.length > 0;
  }

  canPay(detail: PublicSaleLinkDTO): boolean {
    return detail.status === 'ACTIVE';
  }

  /** Liga vigente cuya fecha de vencimiento ya pasó. */
  isOverdue(detail: PublicSaleLinkDTO): boolean {
    if (!detail.expiresAt || detail.status === 'PAID' || detail.status === 'CANCELLED' || detail.status === 'EXPIRED') {
      return false;
    }
    const expiresAt = new Date(detail.expiresAt).getTime();
    return !Number.isNaN(expiresAt) && expiresAt < Date.now();
  }

  pay(detail: PublicSaleLinkDTO): void {
    const hash = this.route.snapshot.queryParamMap.get('hash');
    if (!hash || !this.canPay(detail)) {
      return;
    }
    this.paying.set(true);
    this.saleLinkService.payPublic(detail.id, hash).subscribe({
      next: (response) => {
        const url = response.body?.paymentUrl;
        if (!url) {
          this.paying.set(false);
          this.error.set(true);
          return;
        }
        window.location.href = url;
      },
      error: () => {
        this.paying.set(false);
        this.detail.update((current) => current ? { ...current, status: current.status } : current);
        this.error.set(true);
      }
    });
  }

  formatAmount(amount: number | undefined, currency: string | undefined): string {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: currency || 'MXN' }).format(amount ?? 0);
  }
}
